import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Save, ArrowLeft, Plus, Trash2, ChevronDown, ChevronUp,
  Calendar, Clock, MapPin, Users, DollarSign, Loader2,
  Edit3, Check, X, Eye, GraduationCap, Music
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Collapsible Class Card Component
const ClassCard = ({ classData, onUpdate, onDelete, isExpanded, onToggle }) => {
  const [editing, setEditing] = useState(false);
  const [localData, setLocalData] = useState(classData);

  useEffect(() => {
    setLocalData(classData);
  }, [classData]);

  // Reset editing state when card is collapsed
  useEffect(() => {
    if (!isExpanded) {
      setEditing(false);
    }
  }, [isExpanded]);

  const handleSave = () => {
    onUpdate(localData);
    setEditing(false);
  };

  const handleCancel = () => {
    setLocalData(classData);
    setEditing(false);
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    // Always ensure card is expanded and editing is enabled
    if (!isExpanded) {
      onToggle(); // expand if collapsed
    }
    // Use setTimeout to ensure state updates properly
    setTimeout(() => setEditing(true), 0);
  };

  const handleHeaderClick = (e) => {
    // Don't toggle if clicking on buttons
    if (e.target.closest('button')) return;
    if (!editing) {
      onToggle();
    }
  };

  const updateField = (field, value) => {
    setLocalData(prev => ({ ...prev, [field]: value }));
  };

  const levelColors = {
    beginner: 'bg-green-500/20 text-green-400',
    intermediate: 'bg-yellow-500/20 text-yellow-400',
    advanced: 'bg-red-500/20 text-red-400',
    all: 'bg-blue-500/20 text-blue-400'
  };

  return (
    <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
      {/* Header - Always Visible */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
        onClick={handleHeaderClick}
      >
        <div className="flex items-center gap-4 flex-1">
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${levelColors[localData.level] || levelColors.all}`}>
            {localData.level?.toUpperCase()}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground">{localData.name}</h3>
            <p className="text-sm text-muted-foreground">
              {localData.day_of_week} • {localData.start_time} - {localData.end_time}
            </p>
          </div>
          <div className="text-right text-sm text-muted-foreground hidden md:block">
            <p>{localData.category === 'adult' ? 'Adult' : localData.category === 'kids' ? 'Kids' : 'Senior'}</p>
            <p className="text-primary font-semibold">
              {localData.is_free ? 'FREE' : `$${localData.price_per_class}/class`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4">
          <button
            type="button"
            onClick={handleEditClick}
            className={`p-2 rounded-lg transition-colors ${editing ? 'bg-primary/20 text-primary' : 'hover:bg-white/10 text-muted-foreground hover:text-foreground'}`}
            title="Edit class"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (editing) {
                // If editing, cancel and collapse
                setLocalData(classData);
                setEditing(false);
              }
              onToggle();
            }}
            className="p-2 hover:bg-white/10 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
            title={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-border/30"
          >
            <div className="p-4 space-y-4">
              {editing ? (
                <>
                  {/* Edit Mode */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Class Name</label>
                      <input
                        value={localData.name || ''}
                        onChange={(e) => updateField('name', e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Instructor</label>
                      <input
                        value={localData.instructor || ''}
                        onChange={(e) => updateField('instructor', e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                    <textarea
                      value={localData.description || ''}
                      onChange={(e) => updateField('description', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground resize-none"
                    />
                  </div>

                  <div className="grid md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Day</label>
                      <select
                        value={localData.day_of_week || ''}
                        onChange={(e) => updateField('day_of_week', e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                      >
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                          <option key={day} value={day}>{day}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Start Time</label>
                      <input
                        value={localData.start_time || ''}
                        onChange={(e) => updateField('start_time', e.target.value)}
                        placeholder="7:00 PM"
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">End Time</label>
                      <input
                        value={localData.end_time || ''}
                        onChange={(e) => updateField('end_time', e.target.value)}
                        placeholder="9:00 PM"
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Level</label>
                      <select
                        value={localData.level || 'all'}
                        onChange={(e) => updateField('level', e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                      >
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                        <option value="all">All Levels</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Category</label>
                      <select
                        value={localData.category || 'adult'}
                        onChange={(e) => updateField('category', e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                      >
                        <option value="adult">Adult</option>
                        <option value="kids">Kids</option>
                        <option value="senior">Senior</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Location</label>
                      <input
                        value={localData.location || ''}
                        onChange={(e) => updateField('location', e.target.value)}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Max Students</label>
                      <input
                        type="number"
                        value={localData.max_students || 20}
                        onChange={(e) => updateField('max_students', parseInt(e.target.value) || 20)}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Notes (optional)</label>
                    <input
                      value={localData.notes || ''}
                      onChange={(e) => updateField('notes', e.target.value)}
                      placeholder="Special instructions or requirements..."
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    />
                  </div>

                  {/* Pricing */}
                  <div className="border-t border-border/30 pt-4">
                    <div className="flex items-center gap-4 mb-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={localData.is_free || false}
                          onChange={(e) => updateField('is_free', e.target.checked)}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-sm font-medium text-foreground">This is a FREE class</span>
                      </label>
                    </div>

                    {!localData.is_free && (
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Price Per Class ($)</label>
                          <input
                            type="number"
                            value={localData.price_per_class || 0}
                            onChange={(e) => updateField('price_per_class', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Monthly Price ($)</label>
                          <input
                            type="number"
                            value={localData.price_monthly || 0}
                            onChange={(e) => updateField('price_monthly', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">Yearly Price ($)</label>
                          <input
                            type="number"
                            value={localData.price_yearly || 0}
                            onChange={(e) => updateField('price_yearly', parseFloat(e.target.value) || 0)}
                            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-4 border-t border-border/30">
                    <button
                      onClick={() => onDelete(localData.id)}
                      className="px-4 py-2 text-red-500 hover:bg-red-500/10 rounded-lg font-medium flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Class
                    </button>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={handleCancel}
                        className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg font-medium flex items-center gap-2"
                      >
                        <X className="w-4 h-4" />
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-medium flex items-center gap-2"
                      >
                        <Check className="w-4 h-4" />
                        Save Changes
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* View Mode */}
                  <p className="text-muted-foreground">{localData.description}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">{localData.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">Max {localData.max_students} students</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">{localData.instructor}</span>
                    </div>
                    {localData.notes && (
                      <div className="flex items-center gap-2 col-span-2 md:col-span-1">
                        <span className="text-primary/80 italic text-xs">{localData.notes}</span>
                      </div>
                    )}
                  </div>

                  {!localData.is_free && (
                    <div className="flex gap-6 pt-2 text-sm">
                      <span><span className="text-primary font-bold">${localData.price_per_class}</span> per class</span>
                      <span><span className="text-primary font-bold">${localData.price_monthly}</span> monthly</span>
                      <span><span className="text-primary font-bold">${localData.price_yearly}</span> yearly</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Private Lesson Card Component
const PrivateLessonCard = ({ lesson, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [localData, setLocalData] = useState(lesson);

  const handleSave = () => {
    onUpdate(localData);
    setEditing(false);
  };

  return (
    <div className="bg-card border border-border/50 rounded-xl p-4">
      {editing ? (
        <div className="space-y-3">
          <input
            value={localData.title || ''}
            onChange={(e) => setLocalData(prev => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground font-semibold"
            placeholder="Lesson Title"
          />
          <textarea
            value={localData.description || ''}
            onChange={(e) => setLocalData(prev => ({ ...prev, description: e.target.value }))}
            rows={2}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm resize-none"
            placeholder="Description"
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Price/Hour ($)</label>
              <input
                type="number"
                value={localData.price_per_hour || 0}
                onChange={(e) => setLocalData(prev => ({ ...prev, price_per_hour: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Note</label>
              <input
                value={localData.note || ''}
                onChange={(e) => setLocalData(prev => ({ ...prev, note: e.target.value }))}
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setLocalData(lesson); setEditing(false); }} className="px-3 py-1 text-sm text-muted-foreground hover:text-foreground">
              Cancel
            </button>
            <button onClick={handleSave} className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded-lg">
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h4 className="font-semibold text-foreground">{localData.title}</h4>
            <p className="text-sm text-muted-foreground mt-1">{localData.description}</p>
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span className="text-primary font-bold">${localData.price_per_hour}/hour</span>
              {localData.note && <span className="text-muted-foreground italic">{localData.note}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditing(true)}
              className="p-2 hover:bg-white/10 rounded-lg text-muted-foreground hover:text-foreground"
            >
              <Edit3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(localData.id)}
              className="p-2 hover:bg-red-500/10 rounded-lg text-muted-foreground hover:text-red-500"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminClassEditor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classes, setClasses] = useState([]);
  const [privateLessons, setPrivateLessons] = useState([]);
  const [expandedClass, setExpandedClass] = useState(null);
  const [activeTab, setActiveTab] = useState('classes');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newClass, setNewClass] = useState({
    name: '',
    description: '',
    instructor: 'Mestre Curtis Pierre',
    day_of_week: 'Monday',
    start_time: '7:00 PM',
    end_time: '9:00 PM',
    level: 'all',
    category: 'adult',
    location: 'Escola de Samba Casa Samba Studio',
    is_free: false,
    price_per_class: 20,
    price_monthly: 65,
    price_yearly: 600,
    max_students: 20
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, lessonsRes] = await Promise.all([
        axios.get(`${API}/api/admin/classes/list`),
        axios.get(`${API}/api/admin/classes/private-lessons`)
      ]);
      setClasses(classesRes.data);
      setPrivateLessons(lessonsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateClass = async (updatedClass) => {
    try {
      await axios.put(`${API}/api/admin/classes/class/${updatedClass.id}`, updatedClass);
      setClasses(prev => prev.map(c => c.id === updatedClass.id ? updatedClass : c));
      toast.success('Class updated successfully');
    } catch (error) {
      console.error('Error updating class:', error);
      toast.error('Failed to update class');
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/classes/class/${classId}`);
      setClasses(prev => prev.filter(c => c.id !== classId));
      toast.success('Class deleted successfully');
    } catch (error) {
      console.error('Error deleting class:', error);
      toast.error('Failed to delete class');
    }
  };

  const handleAddClass = async () => {
    if (!newClass.name || !newClass.description) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/api/admin/classes/class`, newClass);
      setClasses(prev => [...prev, { ...newClass, id: response.data.id }]);
      setShowAddModal(false);
      setNewClass({
        name: '',
        description: '',
        instructor: 'Mestre Curtis Pierre',
        day_of_week: 'Monday',
        start_time: '7:00 PM',
        end_time: '9:00 PM',
        level: 'all',
        category: 'adult',
        location: 'Escola de Samba Casa Samba Studio',
        is_free: false,
        price_per_class: 20,
        price_monthly: 65,
        price_yearly: 600,
        max_students: 20
      });
      toast.success('Class added successfully');
    } catch (error) {
      console.error('Error adding class:', error);
      toast.error('Failed to add class');
    }
  };

  const handleUpdatePrivateLesson = async (updatedLesson) => {
    try {
      await axios.put(`${API}/api/admin/classes/private-lesson/${updatedLesson.id}`, updatedLesson);
      setPrivateLessons(prev => prev.map(l => l.id === updatedLesson.id ? updatedLesson : l));
      toast.success('Private lesson updated successfully');
    } catch (error) {
      console.error('Error updating private lesson:', error);
      toast.error('Failed to update private lesson');
    }
  };

  const handleDeletePrivateLesson = async (lessonId) => {
    if (!window.confirm('Are you sure you want to delete this private lesson?')) return;
    
    try {
      await axios.delete(`${API}/api/admin/classes/private-lesson/${lessonId}`);
      setPrivateLessons(prev => prev.filter(l => l.id !== lessonId));
      toast.success('Private lesson deleted successfully');
    } catch (error) {
      console.error('Error deleting private lesson:', error);
      toast.error('Failed to delete private lesson');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  // Group classes by category
  const adultClasses = classes.filter(c => c.category === 'adult');
  const kidsClasses = classes.filter(c => c.category === 'kids');
  const seniorClasses = classes.filter(c => c.category === 'senior');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-card border-b border-border/50 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-white/10 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-foreground">Classes Editor</h1>
              <p className="text-sm text-muted-foreground">{classes.length} classes, {privateLessons.length} private lessons</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/classes"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg font-medium flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview
            </a>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-bold flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Class
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-6 py-4">
        <div className="flex gap-2 border-b border-border/30 mb-6">
          <button
            onClick={() => setActiveTab('classes')}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === 'classes' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Scheduled Classes ({classes.length})
          </button>
          <button
            onClick={() => setActiveTab('private')}
            className={`px-4 py-2 font-medium transition-colors ${activeTab === 'private' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Private Lessons ({privateLessons.length})
          </button>
        </div>

        {activeTab === 'classes' ? (
          <div className="space-y-8">
            {/* Adult Classes */}
            {adultClasses.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  Adult Classes ({adultClasses.length})
                </h2>
                <div className="space-y-3">
                  {adultClasses.map(classData => (
                    <ClassCard
                      key={classData.id}
                      classData={classData}
                      onUpdate={handleUpdateClass}
                      onDelete={handleDeleteClass}
                      isExpanded={expandedClass === classData.id}
                      onToggle={() => setExpandedClass(expandedClass === classData.id ? null : classData.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Kids Classes */}
            {kidsClasses.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <Music className="w-5 h-5 text-green-500" />
                  Kids Classes ({kidsClasses.length})
                </h2>
                <div className="space-y-3">
                  {kidsClasses.map(classData => (
                    <ClassCard
                      key={classData.id}
                      classData={classData}
                      onUpdate={handleUpdateClass}
                      onDelete={handleDeleteClass}
                      isExpanded={expandedClass === classData.id}
                      onToggle={() => setExpandedClass(expandedClass === classData.id ? null : classData.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Senior Classes */}
            {seniorClasses.length > 0 && (
              <div>
                <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-amber-500" />
                  Senior Classes ({seniorClasses.length})
                </h2>
                <div className="space-y-3">
                  {seniorClasses.map(classData => (
                    <ClassCard
                      key={classData.id}
                      classData={classData}
                      onUpdate={handleUpdateClass}
                      onDelete={handleDeleteClass}
                      isExpanded={expandedClass === classData.id}
                      onToggle={() => setExpandedClass(expandedClass === classData.id ? null : classData.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {classes.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No classes yet. Click "Add Class" to create one.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-foreground mb-4">Private Lesson Types</h2>
            {privateLessons.map(lesson => (
              <PrivateLessonCard
                key={lesson.id}
                lesson={lesson}
                onUpdate={handleUpdatePrivateLesson}
                onDelete={handleDeletePrivateLesson}
              />
            ))}
            {privateLessons.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No private lessons configured.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Class Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-card border-b border-border/50 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-foreground">Add New Class</h2>
                <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-white/10 rounded-full">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Class Name *</label>
                    <input
                      value={newClass.name}
                      onChange={(e) => setNewClass(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                      placeholder="e.g., Samba Dance Class"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Instructor</label>
                    <input
                      value={newClass.instructor}
                      onChange={(e) => setNewClass(prev => ({ ...prev, instructor: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Description *</label>
                  <textarea
                    value={newClass.description}
                    onChange={(e) => setNewClass(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground resize-none"
                    placeholder="Describe what students will learn..."
                  />
                </div>

                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Day</label>
                    <select
                      value={newClass.day_of_week}
                      onChange={(e) => setNewClass(prev => ({ ...prev, day_of_week: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    >
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Start Time</label>
                    <input
                      value={newClass.start_time}
                      onChange={(e) => setNewClass(prev => ({ ...prev, start_time: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">End Time</label>
                    <input
                      value={newClass.end_time}
                      onChange={(e) => setNewClass(prev => ({ ...prev, end_time: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Level</label>
                    <select
                      value={newClass.level}
                      onChange={(e) => setNewClass(prev => ({ ...prev, level: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                      <option value="all">All Levels</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Category</label>
                    <select
                      value={newClass.category}
                      onChange={(e) => setNewClass(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    >
                      <option value="adult">Adult</option>
                      <option value="kids">Kids</option>
                      <option value="senior">Senior</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Location</label>
                    <input
                      value={newClass.location}
                      onChange={(e) => setNewClass(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">Max Students</label>
                    <input
                      type="number"
                      value={newClass.max_students}
                      onChange={(e) => setNewClass(prev => ({ ...prev, max_students: parseInt(e.target.value) || 20 }))}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                    />
                  </div>
                </div>

                <div className="border-t border-border/30 pt-4">
                  <label className="flex items-center gap-2 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      checked={newClass.is_free}
                      onChange={(e) => setNewClass(prev => ({ ...prev, is_free: e.target.checked }))}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm font-medium text-foreground">This is a FREE class</span>
                  </label>

                  {!newClass.is_free && (
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Price Per Class ($)</label>
                        <input
                          type="number"
                          value={newClass.price_per_class}
                          onChange={(e) => setNewClass(prev => ({ ...prev, price_per_class: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Monthly Price ($)</label>
                        <input
                          type="number"
                          value={newClass.price_monthly}
                          onChange={(e) => setNewClass(prev => ({ ...prev, price_monthly: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Yearly Price ($)</label>
                        <input
                          type="number"
                          value={newClass.price_yearly}
                          onChange={(e) => setNewClass(prev => ({ ...prev, price_yearly: parseFloat(e.target.value) || 0 }))}
                          className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="sticky bottom-0 bg-card border-t border-border/50 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddClass}
                  className="px-6 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg font-bold flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Class
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminClassEditor;
